import { ref, computed, watch } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import moment from "moment-timezone";
import store from "@/store";
import ability from "@/libs/acl/ability";
import useFilterCounter from "@/components/Filters/useFiltersCount";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { debounce } from "lodash"
import router from '@/router'
import { escapeHtml } from "markdown-it/lib/common/utils";

export default function useList() {
  const { t } = useI18nUtils();
  const trans = {
    "messages.error.server_error": t("messages.error.server_error"),
  };
  const refListTable = ref(null);

  const toast = useToast();

  store.dispatch('tenant/getAll')
  const tenantOptions = computed(() => store.getters['tenant/getSelectedItems'])
  const tenantFilter = ref(null)

  const tableColumns = [
    { label: 'tenants.file.image', key: 'url', sortable: true },
    { label: 'tenants.file.id', key: "id", sortable: true },
    { label: 'tenants.file.name', key: "name", sortable: true },
    { label: "tenants.file.extension", key: "extension", sortable: true },
    { label: 'tenants.file.size', key: 'size', sortable: true },
    { label: 'tenants.file.storage_type', key: 'storage_type', sortable: true },
    { label: 'tenants.file.directory', key: 'directory', sortable: true },
    { label: 'tenants.file.last_access', key: 'last_access', sortable: true },
    { label: 'date.created_at', key: 'created_at', sortable: true, formatter: 'formatDateAssigned' },
    { label: 'date.updated_at', key: 'updated_at', sortable: true, formatter: 'formatDateAssigned' },
  ];

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);

  const sizeMinFilter = ref("")
  const sizeOptions = ref([
    { value: 'mb', label: 'MB'},
    { value: 'gb', label: 'GB'},
    { value: 'tb', label: 'TB'}
  ]);

  const sizeMaxFilter = ref("")
  const createdAtMinFilter = ref("")
  const createdAtMaxFilter = ref("")
  const sizeMinType = ref('mb')
  const sizeMaxType = ref('mb')

  let sizeMin = 0;
  let sizeMax = 0;

  const extensionFilter = ref([])
  const extensionOptions = ref([
    {value: 'jpeg', label: 'jpg'},
    {value: 'png', label: 'png'},
  ]);

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  let idTenant = 0
  if (router.currentRoute.params.id_tenant) idTenant = parseInt(router.currentRoute.params.id_tenant)

  const fetchList = (ctx, callback) => {
    store
      .dispatch("tenant_files/getAll", {
        id_tenant: idTenant,
        per_page: perPage.value,
        page: currentPage.value,
        order_by: sortBy.value,
        sortDesc: isSortDirDesc.value,
        size_min: sizeMin,//sizeMinFilter.value,
        size_max: sizeMax,//sizeMaxFilter.value,
        created_at_min: createdAtMinFilter.value,
        created_at_max: createdAtMaxFilter.value,
        name: searchQuery.value,
        extension: extensionFilter.value
      })
      .then((response) => {
        callback(response.data)
        totalList.value = response.total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: trans["messages.error.server_error"],
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const formatDateAssigned = (value) => {
    const formattedDate = moment(value).format("DD/MM/YYYY HH:mm:ss");
    return formattedDate;
  };

  const refetchData = () => {
    refListTable.value.refresh();
  };

  watch([
    currentPage,
    perPage,
  ], () => {
    refetchData();
  });

  watch([ searchQuery ], debounce(() => { refetchData() }, 500))

  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {

    if(sizeMinType.value == 'mb'){
      sizeMin = (sizeMinFilter.value * 1048576)
    }
    if(sizeMaxType.value == 'mb'){
      sizeMax = (sizeMaxFilter.value * 1048576)
    }

    if(sizeMinType.value == 'gb'){
      sizeMin = (sizeMinFilter.value * 1073741824)
    }
    if(sizeMaxType.value == 'gb'){
      sizeMax = (sizeMaxFilter.value * 1073741824)
    }

    if(sizeMinType.value == 'tb'){
      sizeMin = (sizeMinFilter.value * 1099511627776)
    }
    if(sizeMaxType.value == 'tb'){
      sizeMax = (sizeMaxFilter.value * 1099511627776)
    }

    filters.value = {
      size_min: sizeMin,//sizeMinFilter.value,
      size_max: sizeMax,//sizeMaxFilter.value,
      created_at_min: createdAtMinFilter.value,
      created_at_max: createdAtMaxFilter.value,
      extension: extensionFilter.value
    };
    refetchData();
  }
  const clearSearchFilters = () => {
    sizeMinFilter.value = null
    sizeMaxFilter.value = null
    createdAtMinFilter.value = null
    createdAtMaxFilter.value = null
    extensionFilter.value = null
    buildFilters();
  }
  // ============================================

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,
    tenantOptions,
    tenantFilter,
    refetchData,

    extensionOptions,
    extensionFilter,
    sizeMaxFilter,
    sizeMinFilter,
    sizeOptions,
    createdAtMinFilter,
    //createdAtMinOptions,
    createdAtMaxFilter,
    //createdAtMaxOptions,

    sizeMinType,
    sizeMaxType,

    filtersCount,
    buildFilters,
    clearSearchFilters,
  };
}

